import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import { useTimezone } from '../context/TimezoneContext';
import { formatDateTime } from '../utils/timezone';

const LEVEL_COLORS = {
  fatal: { bg: '#450a0a', color: '#fecaca' },
  error: { bg: '#fef2f2', color: '#991b1b' },
  warn:  { bg: '#fefce8', color: '#854d0e' },
  info:  { bg: '#eff6ff', color: '#1d4ed8' },
  debug: { bg: '#f3f4f6', color: '#6b7280' },
  trace: { bg: '#f9fafb', color: '#9ca3af' },
};

const SOURCE_COLORS = {
  newton_connect: { bg: '#ecfdf5', color: '#065f46', label: 'Newton Connect' },
  ehr_bridge:     { bg: '#eff6ff', color: '#1d4ed8', label: 'EHR Bridge' },
  patient_spa:    { bg: '#fdf2f8', color: '#9d174d', label: 'Patient SPA' },
};

function SystemLogs({ practiceId }) {
  const { displayMode } = useTimezone();
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [filters, setFilters] = useState({
    source_system: '',
    level: '',
    correlation_id: '',
    q: '',
    start_date: '',
    end_date: '',
  });

  // Trace panel state
  const [traceOpen, setTraceOpen] = useState(false);
  const [traceData, setTraceData] = useState(null);
  const [traceLoading, setTraceLoading] = useState(false);
  const [activeCorrelationId, setActiveCorrelationId] = useState(null);

  const limit = 50;

  const loadLogs = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const params = { limit, offset: page * limit };
      if (practiceId) params.practice_id = practiceId;
      if (filters.source_system) params.source_system = filters.source_system;
      if (filters.level) params.level = filters.level;
      if (filters.correlation_id) params.correlation_id = filters.correlation_id;
      if (filters.q) params.q = filters.q;
      if (filters.start_date) params.start_date = filters.start_date;
      if (filters.end_date) params.end_date = filters.end_date;

      const res = await api.getOperationalLogs(params);
      if (res.success) {
        setLogs(res.data || []);
        setTotal(res.pagination?.total || 0);
      }
    } catch (err) {
      setError('Failed to load logs. Ensure the log database is configured.');
    } finally {
      setLoading(false);
    }
  }, [practiceId, page, filters]);

  useEffect(() => { loadLogs(); }, [loadLogs]);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPage(0);
  };

  const openTrace = async (correlationId) => {
    setActiveCorrelationId(correlationId);
    setTraceOpen(true);
    setTraceLoading(true);
    try {
      const res = await api.getLogTrace(correlationId);
      if (res.success) {
        setTraceData(res);
      }
    } catch {
      setTraceData(null);
    } finally {
      setTraceLoading(false);
    }
  };

  const totalPages = Math.ceil(total / limit);

  return (
    <div>
      {/* Header */}
      <div style={s.headerRow}>
        <div>
          <h2 style={s.title}>System Logs</h2>
          <p style={s.subtitle}>
            Operational logs across Newton Connect, EHR Bridge, and Patient SPA
          </p>
        </div>
        <button style={s.refreshBtn} onClick={loadLogs}>Refresh</button>
      </div>

      {error && <div style={s.errorMsg}>{error}</div>}

      {/* Filters */}
      <div style={s.filterRow}>
        <input
          type="text"
          placeholder="Search messages..."
          value={filters.q}
          onChange={(e) => handleFilterChange('q', e.target.value)}
          style={s.searchInput}
        />
        <select
          value={filters.source_system}
          onChange={(e) => handleFilterChange('source_system', e.target.value)}
          style={s.select}
        >
          <option value="">All Systems</option>
          <option value="newton_connect">Newton Connect</option>
          <option value="ehr_bridge">EHR Bridge</option>
          <option value="patient_spa">Patient SPA</option>
        </select>
        <select
          value={filters.level}
          onChange={(e) => handleFilterChange('level', e.target.value)}
          style={s.select}
        >
          <option value="">All Levels</option>
          <option value="error">Error</option>
          <option value="warn">Warning</option>
          <option value="info">Info</option>
          <option value="debug">Debug</option>
        </select>
        <input
          type="text"
          placeholder="Correlation ID..."
          value={filters.correlation_id}
          onChange={(e) => handleFilterChange('correlation_id', e.target.value)}
          style={{ ...s.searchInput, flex: 'none', width: 280, maxWidth: '100%' }}
        />
        <input
          type="date"
          value={filters.start_date}
          onChange={(e) => handleFilterChange('start_date', e.target.value)}
          style={{ ...s.select, minWidth: 130 }}
        />
        <input
          type="date"
          value={filters.end_date}
          onChange={(e) => handleFilterChange('end_date', e.target.value)}
          style={{ ...s.select, minWidth: 130 }}
        />
      </div>

      {/* Table */}
      <div style={s.card}>
        {loading ? (
          <div style={s.empty}>Loading...</div>
        ) : logs.length === 0 ? (
          <div style={s.empty}>No logs found.</div>
        ) : (
          <>
            <div style={{ overflowX: 'auto' }}>
              <table style={s.table}>
                <thead>
                  <tr>
                    <th style={s.th}>Timestamp</th>
                    <th style={s.th}>Source</th>
                    <th style={s.th}>Level</th>
                    <th style={s.th}>Message</th>
                    <th style={s.th}>Path</th>
                    <th style={s.th}>Status</th>
                    <th style={s.th}>Time (ms)</th>
                    <th style={s.th}>Correlation ID</th>
                  </tr>
                </thead>
                <tbody>
                  {logs.map((log) => {
                    const levelColor = LEVEL_COLORS[log.level] || LEVEL_COLORS.info;
                    const sourceInfo = SOURCE_COLORS[log.source_system] || { bg: '#f3f4f6', color: '#374151', label: log.source_system };
                    return (
                      <tr key={log.id} style={s.tr}>
                        <td style={{ ...s.td, whiteSpace: 'nowrap', fontSize: 12 }}>
                          {formatDateTime(log.logged_at, displayMode)}
                        </td>
                        <td style={s.td}>
                          <span style={{ ...s.badge, background: sourceInfo.bg, color: sourceInfo.color }}>
                            {sourceInfo.label}
                          </span>
                        </td>
                        <td style={s.td}>
                          <span style={{ ...s.badge, background: levelColor.bg, color: levelColor.color }}>
                            {log.level}
                          </span>
                        </td>
                        <td style={{ ...s.td, maxWidth: 350, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {log.message}
                        </td>
                        <td style={{ ...s.td, fontSize: 12, color: '#6b7280', whiteSpace: 'nowrap' }}>
                          {log.request_method && `${log.request_method} `}{log.request_path || '—'}
                        </td>
                        <td style={{ ...s.td, textAlign: 'center' }}>
                          {log.status_code ? (
                            <span style={{
                              color: log.status_code >= 500 ? '#dc2626' : log.status_code >= 400 ? '#d97706' : '#059669',
                              fontWeight: 600, fontSize: 12,
                            }}>
                              {log.status_code}
                            </span>
                          ) : '—'}
                        </td>
                        <td style={{ ...s.td, textAlign: 'right', fontSize: 12 }}>
                          {log.response_time_ms != null ? `${log.response_time_ms}` : '—'}
                        </td>
                        <td style={s.td}>
                          {log.correlation_id ? (
                            <button
                              onClick={() => openTrace(log.correlation_id)}
                              style={s.corrIdBtn}
                              title="View full trace"
                            >
                              {log.correlation_id.substring(0, 8)}...
                            </button>
                          ) : '—'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div style={s.pagination}>
                <button style={s.pageBtn} disabled={page === 0} onClick={() => setPage(p => p - 1)}>
                  Previous
                </button>
                <span style={{ fontSize: 13, color: '#6b7280' }}>
                  Page {page + 1} of {totalPages} ({total.toLocaleString()} entries)
                </span>
                <button style={s.pageBtn} disabled={page >= totalPages - 1} onClick={() => setPage(p => p + 1)}>
                  Next
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Trace Slide-out Panel */}
      {traceOpen && (
        <div style={s.traceOverlay} onClick={() => setTraceOpen(false)}>
          <div style={s.tracePanel} onClick={(e) => e.stopPropagation()}>
            <div style={s.tracePanelHeader}>
              <div>
                <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>Correlation Trace</h3>
                <p style={{ margin: '4px 0 0', fontSize: 12, color: '#6b7280', fontFamily: 'monospace' }}>
                  {activeCorrelationId}
                </p>
              </div>
              <button onClick={() => setTraceOpen(false)} style={s.closeBtn}>Close</button>
            </div>

            {traceLoading ? (
              <div style={s.empty}>Loading trace...</div>
            ) : !traceData ? (
              <div style={s.empty}>Failed to load trace.</div>
            ) : (
              <div style={{ padding: '0 16px 16px' }}>
                {/* Systems involved */}
                <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
                  {traceData.systemsInvolved?.map(sys => {
                    const sc = SOURCE_COLORS[sys] || { bg: '#f3f4f6', color: '#374151', label: sys };
                    return (
                      <span key={sys} style={{ ...s.badge, background: sc.bg, color: sc.color }}>
                        {sc.label}
                      </span>
                    );
                  })}
                  <span style={{ fontSize: 12, color: '#6b7280', alignSelf: 'center' }}>
                    {traceData.totalEntries} entries
                  </span>
                </div>

                {/* Trace timeline */}
                {traceData.data?.map((entry, i) => {
                  const lc = LEVEL_COLORS[entry.level] || LEVEL_COLORS.info;
                  const sc = SOURCE_COLORS[entry.source_system] || { bg: '#f3f4f6', color: '#374151', label: entry.source_system };
                  return (
                    <div key={entry.id || i} style={s.traceEntry}>
                      <div style={s.traceTimeline}>
                        <div style={s.traceDot} />
                        {i < traceData.data.length - 1 && <div style={s.traceLine} />}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 4 }}>
                          <span style={{ ...s.badge, background: sc.bg, color: sc.color, fontSize: 10 }}>
                            {sc.label}
                          </span>
                          <span style={{ ...s.badge, background: lc.bg, color: lc.color, fontSize: 10 }}>
                            {entry.level}
                          </span>
                          <span style={{ fontSize: 11, color: '#9ca3af' }}>
                            {formatDateTime(entry.logged_at, displayMode)}
                          </span>
                        </div>
                        <div style={{ fontSize: 13, color: '#111827', marginBottom: 2 }}>
                          {entry.message}
                        </div>
                        {entry.request_path && (
                          <div style={{ fontSize: 11, color: '#6b7280' }}>
                            {entry.request_method} {entry.request_path}
                            {entry.status_code ? ` → ${entry.status_code}` : ''}
                            {entry.response_time_ms != null ? ` (${entry.response_time_ms}ms)` : ''}
                          </div>
                        )}
                        {entry.error_name && (
                          <div style={{ fontSize: 11, color: '#dc2626', marginTop: 2 }}>
                            {entry.error_name}: {entry.error_stack?.split('\n')[0]}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

const s = {
  headerRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 },
  title: { fontSize: 22, fontWeight: 700, color: '#111827', margin: 0 },
  subtitle: { fontSize: 14, color: '#6b7280', margin: '4px 0 0' },
  refreshBtn: {
    padding: '8px 16px', borderRadius: 8, border: '1px solid #d1d5db',
    background: '#fff', fontSize: 13, cursor: 'pointer', fontWeight: 500,
  },
  errorMsg: {
    background: '#fef2f2', color: '#dc2626', padding: '10px 14px',
    borderRadius: 8, marginBottom: 16, fontSize: 13,
  },
  filterRow: { display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap' },
  searchInput: {
    flex: 1, minWidth: 180, padding: '8px 12px', borderRadius: 8,
    border: '1px solid #d1d5db', fontSize: 14, outline: 'none',
  },
  select: {
    padding: '8px 12px', borderRadius: 8, border: '1px solid #d1d5db',
    fontSize: 13, background: '#fff', outline: 'none', minWidth: 130,
  },
  card: {
    background: '#fff', borderRadius: 12, border: '1px solid #e5e7eb', overflow: 'hidden',
  },
  table: { width: '100%', borderCollapse: 'collapse' },
  th: {
    padding: '10px 14px', textAlign: 'left', fontSize: 11, fontWeight: 600,
    color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.5,
    borderBottom: '1px solid #e5e7eb', background: '#f9fafb', whiteSpace: 'nowrap',
  },
  tr: { borderBottom: '1px solid #f3f4f6' },
  td: { padding: '10px 14px', fontSize: 13, color: '#374151', verticalAlign: 'top' },
  badge: {
    padding: '3px 8px', borderRadius: 6, fontSize: 11, fontWeight: 600,
    display: 'inline-block', whiteSpace: 'nowrap',
  },
  corrIdBtn: {
    background: 'none', border: 'none', color: '#4f46e5', cursor: 'pointer',
    fontSize: 12, fontFamily: 'monospace', padding: 0, textDecoration: 'underline',
  },
  empty: { textAlign: 'center', padding: 40, color: '#6b7280' },
  pagination: {
    display: 'flex', justifyContent: 'center', alignItems: 'center',
    gap: 16, padding: '14px 0',
  },
  pageBtn: {
    padding: '6px 14px', borderRadius: 6, border: '1px solid #d1d5db',
    background: '#fff', fontSize: 13, cursor: 'pointer',
  },
  // Trace panel
  traceOverlay: {
    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
    background: 'rgba(0,0,0,0.3)', zIndex: 1000, display: 'flex', justifyContent: 'flex-end',
  },
  tracePanel: {
    width: 520, maxWidth: '90vw', background: '#fff', height: '100%',
    overflowY: 'auto', boxShadow: '-4px 0 20px rgba(0,0,0,0.1)',
  },
  tracePanelHeader: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
    padding: '16px', borderBottom: '1px solid #e5e7eb', position: 'sticky',
    top: 0, background: '#fff', zIndex: 1,
  },
  closeBtn: {
    padding: '6px 12px', borderRadius: 6, border: '1px solid #d1d5db',
    background: '#fff', fontSize: 12, cursor: 'pointer',
  },
  traceEntry: {
    display: 'flex', gap: 12, marginBottom: 0, paddingBottom: 16,
  },
  traceTimeline: {
    display: 'flex', flexDirection: 'column', alignItems: 'center', width: 16,
    flexShrink: 0,
  },
  traceDot: {
    width: 10, height: 10, borderRadius: '50%', background: '#4f46e5',
    border: '2px solid #e0e7ff', flexShrink: 0,
  },
  traceLine: {
    width: 2, flex: 1, background: '#e5e7eb', marginTop: 4,
  },
};

export default SystemLogs;
